import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Typography
} from '@material-tailwind/react';
import ReactSelect from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../config/config';

const OwnerTransactionForm = ({ open, onClose, onSuccess, ownerCode, cars = [] }) => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [formData, setFormData] = useState({
        trans_type: 'REM',
        plate_no: '',
        date: new Date().toISOString().split('T')[0],
        amount: '',
        description: '',
        reference_type: '',
        reference_id: '',
        payment_method: 'CASH',
        payment_reference: '',
        notes: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchCashbookAccounts = useCallback(async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/api/cashbook/accounts`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (response.data && response.data.success) {
                // Accounts are fetched but not stored in state as they're not used
            }
        } catch (error) {
            console.error('Error fetching cashbook accounts:', error);
        }
    }, [authToken]);

    useEffect(() => {
        if (open) {
            fetchCashbookAccounts();
        }
    }, [open, fetchCashbookAccounts]);

    const validate = () => {
        const newErrors = {};
        if (!formData.trans_type) newErrors.trans_type = 'Transaction type is required';
        if (!formData.date) newErrors.date = 'Date is required';
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Amount must be greater than zero';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/api/owners/${ownerCode}/transactions`,
                {
                    ...formData,
                    amount: parseFloat(formData.amount),
                    input_by: user?.username || 'system'
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                onSuccess && onSuccess(response.data.data);
                handleClose();
            } else {
                setErrors({ submit: response.data?.message || 'Failed to create transaction' });
            }
        } catch (error) {
            setErrors({ submit: error.response?.data?.message || 'Error creating transaction' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            trans_type: 'REM',
            plate_no: '',
            date: new Date().toISOString().split('T')[0],
            amount: '',
            description: '',
            reference_type: '',
            reference_id: '',
            payment_method: 'CASH',
            payment_reference: '',
            notes: ''
        });
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} handler={onClose} size="lg">
            <DialogHeader>Add Owner Transaction</DialogHeader>
            <form onSubmit={handleSubmit} id="owner-trans-form">
                <DialogBody className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type *</label>
                            <ReactSelect
                                options={[
                                    { value: 'REM', label: 'Remittance' },
                                    { value: 'PAY', label: 'Payment' },
                                    { value: 'RIM', label: 'Reimbursement' },
                                    { value: 'CHG', label: 'Charge' },
                                    { value: 'ADJ', label: 'Adjustment' }
                                ]}
                                value={{ value: formData.trans_type, label: formData.trans_type === 'REM' ? 'Remittance' : formData.trans_type === 'PAY' ? 'Payment' : formData.trans_type === 'RIM' ? 'Reimbursement' : formData.trans_type === 'CHG' ? 'Charge' : 'Adjustment' }}
                                onChange={(option) => setFormData(prev => ({ ...prev, trans_type: option.value }))}
                                isSearchable
                                isClearable={false}
                            />
                            {errors.trans_type && <Typography color="red" variant="small">{errors.trans_type}</Typography>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Car (Optional)</label>
                            <ReactSelect
                                options={[
                                    { value: '', label: 'None (Owner-level)' },
                                    ...cars.map(car => ({ value: car.plate_no, label: car.plate_no }))
                                ]}
                                value={formData.plate_no ? { value: formData.plate_no, label: formData.plate_no } : { value: '', label: 'None (Owner-level)' }}
                                onChange={(option) => setFormData(prev => ({ ...prev, plate_no: option?.value || '' }))}
                                isSearchable
                                isClearable={false}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            type="date"
                            label="Date *"
                            value={formData.date}
                            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                            error={!!errors.date}
                        />
                        <Input
                            type="number"
                            label="Amount *"
                            value={formData.amount}
                            onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                            error={!!errors.amount}
                            step="0.01"
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            {/* <label className="block text-sm font-medium text-gray-700 mb-2">Reference Type</label> */}
                            <ReactSelect
                                options={[
                                    { value: '', label: 'None' },
                                    { value: 'WEEKLY_REMITTANCE', label: 'Weekly Remittance' },
                                    { value: 'JOB_CARD', label: 'Job Card' },
                                    { value: 'EXPENSE', label: 'Expense' },
                                    { value: 'MANUAL', label: 'Manual' }
                                ]}
                                value={formData.reference_type ? { value: formData.reference_type, label: formData.reference_type === 'WEEKLY_REMITTANCE' ? 'Weekly Remittance' : formData.reference_type === 'JOB_CARD' ? 'Job Card' : formData.reference_type === 'EXPENSE' ? 'Expense' : 'Manual' } : { value: '', label: 'None' }}
                                onChange={(option) => setFormData(prev => ({ ...prev, reference_type: option?.value || '' }))}
                                isSearchable
                                isClearable={false}
                            />
                        </div>
                        <Input
                            label="Reference ID"
                            value={formData.reference_id}
                            onChange={(e) => setFormData(prev => ({ ...prev, reference_id: e.target.value }))}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            {/* <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label> */}
                            <ReactSelect
                                options={[
                                    { value: 'CASH', label: 'Cash' },
                                    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
                                    { value: 'MPESA', label: 'M-Pesa' },
                                    { value: 'CHEQUE', label: 'Cheque' },
                                    { value: 'CREDIT_CARD', label: 'Credit Card' }
                                ]}
                                value={{ value: formData.payment_method, label: formData.payment_method === 'CASH' ? 'Cash' : formData.payment_method === 'BANK_TRANSFER' ? 'Bank Transfer' : formData.payment_method === 'MPESA' ? 'M-Pesa' : formData.payment_method === 'CHEQUE' ? 'Cheque' : 'Credit Card' }}
                                onChange={(option) => setFormData(prev => ({ ...prev, payment_method: option.value }))}
                                isSearchable
                                isClearable={false}
                            />
                        </div>
                        <Input
                            label="Payment Reference"
                            value={formData.payment_reference}
                            onChange={(e) => setFormData(prev => ({ ...prev, payment_reference: e.target.value }))}
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            rows={2}
                        />
                    </div>

                    {errors.submit && (
                        <Typography color="red" variant="small">
                            {errors.submit}
                        </Typography>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={onClose}
                        className="mr-2"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="owner-trans-form"
                        color="blue"
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
};

export default OwnerTransactionForm;

